import type { ReactNode } from 'react';
import type { Player } from '../../shared/types';
import { avatarUrl } from '../lib/format';
import type { TeamView } from '../lib/league';
import { Link } from '../lib/router';

export function Loading({ label = 'Loading league data' }: { label?: string }) {
  return (
    <div className="center-state">
      <div className="spinner" />
      <div>{label}…</div>
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="center-state">
      <div>
        <strong>Could not reach the league data.</strong>
      </div>
      <div className="faint" style={{ fontSize: 13, maxWidth: '42ch' }}>
        {message}
      </div>
      {onRetry && (
        <button className="retry" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Avatar({
  id,
  alt,
  large = false,
}: {
  id: string | null;
  alt: string;
  large?: boolean;
}) {
  const url = avatarUrl(id);
  const cls = large ? 'avatar avatar-lg' : 'avatar';

  if (!url) {
    // Deterministic initial fallback keeps the row height stable when a
    // manager has never uploaded an avatar.
    return (
      <div
        className={cls}
        aria-hidden="true"
        style={{
          display: 'grid',
          placeItems: 'center',
          fontFamily: 'var(--display)',
          fontSize: large ? 20 : 14,
          color: 'var(--text-faint)',
        }}
      >
        {alt.slice(0, 1).toUpperCase()}
      </div>
    );
  }

  return <img className={cls} src={url} alt="" loading="lazy" width={large ? 44 : 30} height={large ? 44 : 30} />;
}

export function PositionTag({ position }: { position: string }) {
  return <span className={`pos pos-${position}`}>{position}</span>;
}

export function TeamCell({ team, link = true }: { team: TeamView; link?: boolean }) {
  const inner = (
    <div className="team-cell">
      <Avatar id={team.avatar} alt={team.name} />
      <div className="team-names">
        <div className="team-title">{team.name}</div>
        <div className="team-handle">@{team.manager}</div>
      </div>
    </div>
  );

  return link ? <Link to={`/teams/${team.roster_id}`}>{inner}</Link> : inner;
}

export function PlayerRow({ player, slot }: { player: Player | null; slot?: string }) {
  if (!player) {
    return (
      <div className="player-row">
        {slot && <span className="slot-label">{slot}</span>}
        <span className="faint">Empty</span>
      </div>
    );
  }

  return (
    <div className="player-row">
      {slot && <span className="slot-label">{slot}</span>}
      <PositionTag position={player.position} />
      <span className="player-name">{player.name}</span>
      {player.injuryStatus && <span className="injury">{player.injuryStatus}</span>}
      <span className="player-meta">
        {player.team ?? 'FA'}
        {player.age != null && ` · ${player.age}y`}
      </span>
    </div>
  );
}

export function Stat({
  label,
  value,
  note,
}: {
  label: string;
  value: ReactNode;
  note?: ReactNode;
}) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {note && <div className="stat-note">{note}</div>}
    </div>
  );
}

/**
 * A page title and a dateline.
 *
 * `meta` is the preferred subtitle: a list of facts about the data on the page,
 * rendered dot-separated, in the register a reference site uses ("81 games ·
 * 2025-present"). It exists because the prose version of the same information
 * reads as filler -- a sentence has to be *about* something, so an explanatory
 * subtitle inevitably ends up explaining what the reader can already see.
 *
 * `children` is the escape hatch for the rare page that genuinely needs a
 * sentence. Reach for it only when a fact cannot be stated as one; falsy
 * entries in `meta` are dropped, so conditional facts need no wrapper.
 */
export function PageHead({
  eyebrow,
  title,
  meta,
  children,
}: {
  eyebrow?: string;
  title: string;
  meta?: Array<ReactNode | null | false | undefined>;
  children?: ReactNode;
}) {
  const facts = (meta ?? []).filter(Boolean);
  return (
    <header className="page-head">
      {eyebrow && <div className="eyebrow">{eyebrow}</div>}
      <h1>{title}</h1>
      {facts.length > 0 && (
        <p className="page-meta">
          {facts.map((fact, i) => (
            <span key={i}>{fact}</span>
          ))}
        </p>
      )}
      {children && <p>{children}</p>}
    </header>
  );
}

/**
 * A glossary for columns whose meaning is not obvious from the header.
 *
 * Deliberately a definition list rather than a paragraph. The prose version
 * makes a reader who is squinting at one column read past the other four to
 * find it, and it invites every definition to grow a justifying clause. Here
 * the term sits next to its meaning and there is nowhere for a clause to go.
 */
export function Legend({
  items,
}: {
  // Falsy entries are dropped, exactly as in `PageHead`'s `meta`. A term that
  // only applies to some states of a page (a column that appears once games
  // have been played) can then be written inline as `cond && {...}` rather
  // than assembled into an array above the JSX.
  items: Array<{ term: string; def: ReactNode } | null | false | undefined>;
}) {
  const entries = items.filter((i): i is { term: string; def: ReactNode } => Boolean(i));
  return (
    <dl className="glossary">
      {entries.map((item) => (
        <div key={item.term}>
          <dt>{item.term}</dt>
          <dd>{item.def}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="section">
      <div className="section-head">
        <h2>{title}</h2>
        {hint && <span className="hint">{hint}</span>}
      </div>
      {children}
    </section>
  );
}

/** Gold/silver/bronze colouring for the first three finishers. */
export function medalClass(place: number): string {
  return place <= 3 ? `medal-${place}` : '';
}
